import React from 'react';
import { Activity, Loader2 } from 'lucide-react';

// CDS Logo Spinner Component
const CDSLogoSpinner = ({ size = "default" }) => {
  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return { container: "w-8 h-8", fontSize: "6" };
      case "lg":
        return { container: "w-16 h-16", fontSize: "12" };
      case "xl":
        return { container: "w-24 h-24", fontSize: "18" };
      default:
        return { container: "w-12 h-12", fontSize: "9" };
    }
  };

  const sizeClasses = getSizeClasses();

  return (
    <div className={`${sizeClasses.container} relative`}>
      <svg 
        width="100%" 
        height="100%" 
        viewBox="0 0 32 32" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        className="animate-spin"
      >
        <defs>
          <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{stopColor:"#3B82F6", stopOpacity:1}} />
            <stop offset="100%" style={{stopColor:"#1E40AF", stopOpacity:1}} />
          </linearGradient>
        </defs>
        
        {/* Background circle */}
        <circle cx="16" cy="16" r="16" fill="url(#bgGradient)"/>
        
        {/* CDS Text */}
        <text x="16" y="20" fontFamily="Arial, sans-serif" fontSize={sizeClasses.fontSize} fontWeight="bold" textAnchor="middle" fill="white">CDS</text>
      </svg>
      
      {/* Outer ring animation */}
      <div className="absolute inset-0 rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin"></div>
    </div>
  );
};

const Loading = ({ 
  message = "กำลังโหลด...", 
  size = "default",
  variant = "cds",
  className = ""
}) => {
  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return {
          container: "py-4",
          icon: "h-4 w-4",
          text: "text-sm"
        };
      case "lg":
        return {
          container: "py-12",
          icon: "h-8 w-8",
          text: "text-lg"
        };
      case "xl":
        return {
          container: "py-16",
          icon: "h-12 w-12",
          text: "text-xl"
        };
      default:
        return {
          container: "py-8",
          icon: "h-6 w-6",
          text: "text-base"
        };
    }
  };

  const sizeClasses = getSizeClasses();

  const renderIcon = () => {
    if (variant === "dots") {
      return (
        <div className="flex space-x-1">
          <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
          <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>
      );
    }
    
    if (variant === "pulse") {
      return (
        <div className="relative">
          <div className={`${sizeClasses.icon} bg-blue-600 rounded-full animate-pulse`}></div>
          <div className={`${sizeClasses.icon} bg-blue-300 rounded-full animate-ping absolute top-0 left-0`}></div>
        </div>
      );
    }
    
    if (variant === "activity") {
      return <Activity className={`${sizeClasses.icon} animate-spin text-blue-600`} />;
    }
    
    if (variant === "loader2") {
      return <Loader2 className={`${sizeClasses.icon} animate-spin text-blue-600`} />;
    }
    
    if (variant === "cds") {
      return <CDSLogoSpinner size={size} />;
    }
    
    // Default CDS logo spinner
    return <CDSLogoSpinner size={size} />;
  };

  return (
    <div className={`flex flex-col items-center justify-center ${sizeClasses.container} ${className}`}>
      <div className="flex items-center space-x-3">
        {renderIcon()}
        <span className={`${sizeClasses.text} text-gray-600 font-medium`}>
          {message}
        </span>
      </div>
    </div>
  );
};

// Full screen loading component
export const FullScreenLoading = ({ 
  message = "กำลังโหลด...", 
  variant = "cds",
  className = ""
}) => {
  return (
    <div className={`fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50 ${className}`}>
      <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-200">
        <Loading 
          message={message} 
          size="lg" 
          variant={variant}
        />
      </div>
    </div>
  );
};

// Inline loading component
export const InlineLoading = ({ 
  message = "กำลังโหลด...", 
  variant = "cds",
  className = ""
}) => {
  return (
    <Loading 
      message={message} 
      size="sm" 
      variant={variant}
      className={className}
    />
  );
};

// Page loading component
export const PageLoading = ({ 
  message = "กำลังโหลดข้อมูล...", 
  variant = "cds",
  className = ""
}) => {
  return (
    <div className={`min-h-screen flex items-center justify-center bg-gray-50 ${className}`}>
      <div className="text-center">
        <Loading 
          message={message} 
          size="lg" 
          variant={variant}
        />
      </div>
    </div>
  );
};

export default Loading;
